import React, { useState, useEffect } from "react";
import { SiteLink } from "../../components";
import { useStore } from "../../store";
import { useAuth } from "../../auth";
import { FaPlus } from "react-icons/fa";
import { FaMinus } from "react-icons/fa";
import { BiSearchAlt } from "react-icons/bi";
import { SearchBar } from "./searchBar2";
import { LIVE_FEEDS_PAGE_PATH, DARTFISH_PAGE_PATH } from "./liveFeedNavPaths";
import env from "../../env";

/** Returns true once we confirm the logged-in user has purchased a season pass. */
function useHasSeasonPass(user) {
  const [hasSeasonPass, setHasSeasonPass] = useState(false);

  useEffect(() => {
    if (!user) {
      setHasSeasonPass(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const token = await user.getIdToken();
        const url = (env.apiBase || '').replace(/\/$/, '') + '/api/order/MyOrders';
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok || cancelled) return;
        const orders = await res.json();
        if (!Array.isArray(orders) || cancelled) return;
        const found = orders.some((order) =>
          (order.orderItems || order.OrderItems || []).some((item) => {
            const name = (item.itemName || item.productName || '').toLowerCase();
            return /season pass|all season/i.test(name);
          })
        );
        if (!cancelled) setHasSeasonPass(found);
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [user]);

  return hasSeasonPass;
}

export function NavBarHori(props) {
  const { state } = useStore();
  const currentLocation =
    state.history && state.history.length > 0
      ? state.history[state.history.length - 1].path
      : "";
  const auth = useAuth();
  const menu = state.menu || { items: [] };
  const [searchOpened, setSearchOpened] = useState(false);
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const hasSeasonPass = useHasSeasonPass(auth.user);

  // Close dropdowns when navigating
  useEffect(() => {
    setSubscribeOpen(false);
    setAccountOpen(false);
  }, [currentLocation]);

  const depth = 1;
  const menuItemElts = menu.items.map((item, index) => (
    <React.Fragment key={index}>
      <MenuItem
        item={item}
        depth={depth}
        onCloseMenu={props.onCloseMenu}
        currentLocation={currentLocation}
      />
    </React.Fragment>
  ));

  const signInHref = '/login?returnUrl=' + encodeURIComponent('/members-area');

  const subscribeDropdown = (
    <div className='hmenu_dropdown' style={{ position: 'relative' }}>
      <button
        type="button"
        className="horizontal_menu_cta horizontal_menu_cta--primary"
        onClick={() => { setSubscribeOpen(o => !o); setAccountOpen(false); }}
        aria-haspopup="true"
        aria-expanded={subscribeOpen}
      >
        Subscribe ▾
      </button>
      {subscribeOpen && (
        <div className='hmenu_dropdown__panel' role="menu">
          <SiteLink
            to={LIVE_FEEDS_PAGE_PATH}
            className='hmenu_dropdown__item'
            role="menuitem"
            onClick={() => { setSubscribeOpen(false); props.onCloseMenu && props.onCloseMenu(); }}
          >
            Live Stream
          </SiteLink>
          <SiteLink
            to={DARTFISH_PAGE_PATH}
            className='hmenu_dropdown__item'
            role="menuitem"
            onClick={() => { setSubscribeOpen(false); props.onCloseMenu && props.onCloseMenu(); }}
          >
            Adelaide Footy TV
          </SiteLink>
        </div>
      )}
    </div>
  );

  const horizontalMenuActions =
    auth.user ? (
      <div className='horizontal_menu_actions'>
        <div className='hmenu_dropdown' style={{ position: 'relative' }}>
          <button
            type="button"
            className="horizontal_menu_cta horizontal_menu_cta--secondary"
            onClick={() => { setAccountOpen(o => !o); setSubscribeOpen(false); }}
            aria-haspopup="true"
            aria-expanded={accountOpen}
          >
            My account ▾
          </button>
          {accountOpen && (
            <div className='hmenu_dropdown__panel' role="menu">
              <SiteLink
                to='/members-area'
                className='hmenu_dropdown__item'
                role="menuitem"
                onClick={() => { setAccountOpen(false); props.onCloseMenu && props.onCloseMenu(); }}
              >
                My account
              </SiteLink>
              <button
                type="button"
                className='hmenu_dropdown__item hmenu_dropdown__item--btn'
                role="menuitem"
                onClick={() => { setAccountOpen(false); auth.signout(); }}
              >
                Log out
              </button>
            </div>
          )}
        </div>
        {!hasSeasonPass && subscribeDropdown}
      </div>
    ) : (
      <div className='horizontal_menu_actions'>
        <SiteLink to={signInHref} className="horizontal_menu_cta horizontal_menu_cta--secondary" onClick={props.onCloseMenu}>Sign In</SiteLink>
        {subscribeDropdown}
      </div>
    );

  if (searchOpened) {
    return (
      <div className='horizontal_menu'>
        <ul className='horizontal_menu_ul'>{menuItemElts} </ul>
        {horizontalMenuActions}
        <div
          className={`search_container ${
            searchOpened ? "search_container_open" : ""
          }`}
          onClick={() => setSearchOpened(!searchOpened)}
        >
          <BiSearchAlt size={"25"} />
          <span>SEARCH</span>
        </div>
        <SearchBar
          onCloseMenu={() => setSearchOpened(false)}
          setSearchOpened={setSearchOpened}
        />
      </div>
    );
  } else {
    return (
      <div className='horizontal_menu'>
        <ul className='horizontal_menu_ul'>{menuItemElts}</ul>
        {horizontalMenuActions}
        <div className='search_container' onClick={() => setSearchOpened(true)}>
          <BiSearchAlt size={"25"} />
          <span>SEARCH</span>
        </div>
      </div>
    );
  }
}

function MenuItem(props) {
  const [subMenuOpened, setSubMenuOpened] = useState(false);
  const item = props.item;
  const subItems = item.items || [];
  const currentLocation = props.currentLocation;

  const subItemsElts =
    subItems.length > 0
      ? subItems.map((subItem, index) => (
          <SubMenuItem
            item={subItem}
            key={index}
            onCloseMenu={props.onCloseMenu}
            level={0}
          />
        ))
      : null;

  useEffect(() => {
    setSubMenuOpened(false);
  }, [currentLocation]);

  function closeMenu() {
    props.onCloseMenu();
  }

  if (item.url) {
    return (
      <li
        className={`horizontal_menu_item ${
          subMenuOpened ? "horizontal_menu_item--open" : ""
        }`}
      >
        <div className='horizontal_menu_link'>
          <SiteLink to={item.url} onClick={() => closeMenu()}>
            {item.title}
          </SiteLink>
        </div>
        {subItemsElts && (
          <ul className={`horizontal_subMenu`}>{subItemsElts}</ul>
        )}
      </li>
    );
  } else {
    return (
      <li
        className={`horizontal_menu_item ${
          subMenuOpened ? "horizontal_menu_item--open" : ""
        }`}
      >
        <div className='horizontal_menu_link'>
          <span>{item.title}</span>
        </div>
        {subItemsElts && (
          <ul className={`horizontal_subMenu`}>{subItemsElts}</ul>
        )}
      </li>
    );
  }
}

function SubMenuItem(props) {
  const item = props.item;
  const level = props.level + 1;
  const childItem = item.items || [];
  const [subMenuOpened, setSubMenuOpened] = useState(false);
  const childElts = childItem.map((item, index) => {
    return (
      <SubMenuItem
        key={index}
        item={item}
        onCloseMenu={props.onCloseMenu}
        level={level}
      />
    );
  });

  function closeMenu() {
    props.onCloseMenu();
  }

  function toggleSubmenu(e) {
    e.preventDefault();
    e.stopPropagation();
    setSubMenuOpened(!subMenuOpened);
  }

  return (
    <li
      className={`level__${level} navBar__subItem ${level > 1 && "sub__level"}`}
    >
      <SiteLink
        className='navBar__subLink'
        to={item.url}
        onClick={() => closeMenu()}
      >
        <span>{item.title}</span>
        {childElts && childElts.length > 0 && (
          <span
            className={`navBar__icon__sub ${
              subMenuOpened ? "navBar__icon__sub--open" : ""
            }`}
            onClick={(e) => toggleSubmenu(e)}
          >
            {" "}
            {subMenuOpened ? <FaMinus /> : <FaPlus />}{" "}
          </span>
        )}
      </SiteLink>
      {childElts && childElts.length > 0 && (
        <ul
          className={`level__${level + 1} navBar__subMenu ${
            level + 1 > 1 && "sub__level"
          } ${level + 1 > 6 && "over__level"} ${
            subMenuOpened ? "navBar__subMenu--open" : ""
          }`}
        >
          {childElts}
        </ul>
      )}
    </li>
  );
}
